// src/hooks/useRecipes.js
import { useState, useRef, useCallback } from 'react';
import { fetchRecipes } from '../services/recipeService';

export function useRecipes({ consume } = {}) {
  const [recipes, setRecipes]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Simple in-memory cache: { "egg,chicken,..." : [...recipes] }
  const cache = useRef({});

  const search = useCallback(async (ingredientNames, stapleLabels = [], mealType = null) => {
    if (ingredientNames.length < 2) return;

    // Rate limit check — consume() returns false if over limit
    if (consume && !consume()) return;

    const stapleSet = new Set(stapleLabels.map(l => l.toLowerCase()));
    const nonStapleNames = ingredientNames.filter(n => !stapleSet.has(n.toLowerCase()));

    // Cache key based only on non-staple ingredients — different staple combos
    // with same real ingredients should return same cached results
    const cacheKey = [...nonStapleNames].sort().join(',') || [...ingredientNames].sort().join(',');

    if (cache.current[cacheKey]) {
      setRecipes(cache.current[cacheKey]);
      setHasSearched(true);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const results = await fetchRecipes(ingredientNames, mealType);
      // Require every returned recipe to use at least 2 of the user's non-staple ingredients.
      // This ensures results are actually relevant to what the user picked, not just
      // staple-only matches.
      const MIN_NON_STAPLE_HITS = nonStapleNames.length >= 2 ? 2 : 1;

      const filtered = results.filter(r => {
        const recipeIngs = (r.ingredientDetails || []).map(i => (i.name || '').toLowerCase());
        const hits = nonStapleNames.filter(n =>
          recipeIngs.some(ri => ri.includes(n.toLowerCase()) || n.toLowerCase().includes(ri))
        ).length;
        return hits >= MIN_NON_STAPLE_HITS;
      });

      // Score and sort: count how many non-staple ingredients each recipe uses.
      // Recipes using more of the user's real (non-staple) picks rank higher.
      const scored = filtered.map(r => {
        const recipeIngs = (r.ingredientDetails || []).map(i => (i.name || '').toLowerCase());
        const nonStapleHits = nonStapleNames.filter(n =>
          recipeIngs.some(ri => ri.includes(n.toLowerCase()) || n.toLowerCase().includes(ri))
        ).length;
        return { ...r, _nonStapleHits: nonStapleHits };
      }).sort((a, b) => {
        // Primary: non-staple hits descending
        if (b._nonStapleHits !== a._nonStapleHits) return b._nonStapleHits - a._nonStapleHits;
        // Secondary: overall matchPercent descending
        return b.matchPercent - a.matchPercent;
      }).map(({ _nonStapleHits, ...r }) => r); // strip internal field

      cache.current[cacheKey] = scored;
      setRecipes(scored);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  }, [consume]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback(() => {
    setRecipes([]);
    setError(null);
    setHasSearched(false);
  }, []);

  return { recipes, loading, error, hasSearched, search, reset };
}
